import {
  FC,
  SetStateAction,
  useLayoutEffect,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { isCancel } from 'axios';
import { Form, Button, Row, Col } from 'antd';
import { useRouter } from 'next/router';
import { getQuoteData } from '@/services/quote';
import styles from './QuoteCardMyMillion.module.scss';
import { useQuoteCardMobile } from '@/hooks/use-quote-card-mobile';
import { QuoteCardMyMillionProps, planItemProps } from './QuoteCardMyMillion.types';
import { getProductPlanConfig, PRODUCT_ID } from '@/constants/productConfig';
import PromotionHeader from '../PromotionHeader';
import LinkButton from '../LinkButton';
import SelectBlock from '../SelectBlock';
import BrochureLink from '../BrochureLink';
import CircleRadioGroup from '@/components/CircleRadioGroup';
import PriceSliderDeductible from '../PriceSliderDeductible';
import TimeSwitch from '@/components/TimeSwitch';
import PlanCard from '@/components/PlanCard';
import PromotionQuote from '../PromotionQuote';
import { useConfigurationV2MockData } from '@/hooks/use-configuration-v2-mock-data';

let controller: AbortController;

/*
  初始化:(获取defalteData)
    quoteCardData: CMS 数据，获取卡片的头图；CTA的link等内容
    productConfig: 获取表单默认选项（性别，年龄，投保额，选择的plan，plan的类型）
    promotionData: 获取优惠码
 
  数据更新:
   调用getQoute：(每次设置好表单选项或优惠码，包括首次设置)，每个plan获取一次
    - 触发时机：用户更改表单数据，用户获取新的优惠码，首次获取（做成依赖项）
    - 参数：表单数据，优惠码
    - 返回：顶部的优惠券+不同plan的实际金额
    - 更新 PromotionHeader component中的优惠券名称 + 倒数日期 -> 放到promotionHeaderData
 
   更新优惠码：
    调用新的优惠码，并更新
 
  CTA button: 点击立即投保，并且把参数带上跳转
*/

const QuoteCardMyMillion = memo((props: QuoteCardMyMillionProps) => {
  const { quoteCardData, promotionData, productConfig } = props;
  const router = useRouter();
  const [form] = Form.useForm();
  const isMobile = useQuoteCardMobile();
  // const configurationV2Data = useConfigurationV2MockData();
  // const vPrimeMockData = JSON.parse(
  //   configurationV2Data.filter((item: { key: string }) => {
  //     if (item.key === 'MMP') {
  //       return item;
  //     }
  //   })[0].value
  // );
  // const productConfig = vPrimeMockData;
  const [loading, setLoading] = useState<boolean>(false);
  const [productPlans, setProductPlans] = useState<planItemProps[]>(productConfig?.plans);
  const [promotionQuote, setPromotionQuote] = useState<any>({});
  const [promotionHeaderData, setPromotionHeaderData] = useState<any>({});
  const [ageOption, setAgeOption] = useState<any[]>([]);
  const [quoteParams, setQuoteParams] = useState<any>({});
  const [errorQuote, setErrorQuote] = useState<string>('');
  const [selectedPlan, setSelectedPlan] = useState<any>({});

  // basic quote params(not change part) for get quote api
  const basicQuoteParams = useMemo(() => {
    return {
      productId: PRODUCT_ID.MYMILLION,
      optionCode: '',
      language: 'ZH',
      renewalTerm: ''
    };
  }, []);

  // if form values change, should update the quote params
  const onFormDataChange = () => {
    const { age, gender, paymentFrequency, deductible } = form.getFieldsValue();
    setQuoteParams((QParams: any) => ({
      ...QParams,
      age,
      gender,
      paymentFrequency,
      deductible
    }));
  };

  // if promo code change,should update the curPromoCode
  const onPromotionCodeChange = useCallback((promotionCode: any) => {
    setQuoteParams((QParams: any) => ({
      ...QParams,
      promotionCode
    }));
  }, []);

  // select plan card
  const changePlanCard = (selectedCard: any) => {
    setSelectedPlan(selectedCard);
  };

  // get product plans
  const fetchQuote = useCallback(async () => {
    controller?.abort();
    // Generate new abort controller
    controller = new AbortController();
    setLoading(true);
    setErrorQuote('');
    const { age, gender, paymentFrequency, deductible, promotionCode } = quoteParams;
    const planPromiseArr =
      productConfig?.plans?.length &&
      productConfig?.plans?.map(async (planItem: any) => {
        const curPlanConfig = getProductPlanConfig(PRODUCT_ID.MYMILLION, planItem.code, {
          sumInsured: productConfig?.sum_insured?.default_value,
          deductible,
          renewablePeriod: ''
        });
        return await getQuoteData(
          {
            ...basicQuoteParams,
            planCode: curPlanConfig?.planCode ?? '',
            promotionCode,
            quoteInputDetails: {
              classes: curPlanConfig?.classes,
              sumInsuredCcy: 'HKD',
              occupation: '',
              smoker: false,
              sumInsured: 0,
              age,
              gender,
              paymentFrequency
            }
          },
          {
            signal: controller.signal
          }
        ).then((QuoteRes: any) => {
          if (selectedPlan.code === planItem.code) {
            setSelectedPlan((sPlan: any) => ({
              ...sPlan,
              price: QuoteRes?.data?.quoteDetails?.netPremium,
              originalPrice: QuoteRes?.data?.quoteDetails?.grossPremium
            }));
          }
          return {
            ...planItem,
            ...QuoteRes?.data.quoteDetails,
            shouldShowQuoteLabel: false,
            qouteLabel: QuoteRes?.data?.quoteDetails?.promotion?.shortDescZh,
            price: QuoteRes?.data?.quoteDetails?.netPremium,
            originalPrice: QuoteRes?.data?.quoteDetails?.grossPremium
          };
        });
      });
    const response = await Promise.allSettled(planPromiseArr);
    const hasCancel = response.some((x: any) => isCancel(x.reason));
    if (hasCancel) return;
    const isApplyPromoCodeFailed = promotionCode
      ? response.every(x => (x.status === 'fulfilled' ? !x.value?.promotion : !isCancel(x.reason)))
      : false;
    setErrorQuote(isApplyPromoCodeFailed ? 'error' : 'no-error');
    const plans = response.map((item: any) => item?.value);
    setProductPlans(plans);
    if (isApplyPromoCodeFailed) {
      setPromotionQuote({});
      setPromotionHeaderData({});
    } else {
      setPromotionHeaderData(plans[0].promotion);
      setPromotionQuote({
        ...plans[0].promotion,
        promotionCode
      });
    }
    setLoading(false);
  }, [basicQuoteParams, productConfig, quoteParams, selectedPlan]);

  // 自付費 or 投保额
  const isDeductible = useMemo(() => {
    return productConfig.sum_insured.minimum_value === productConfig.sum_insured.maximum_value;
  }, []);

  //初始化Form Value
  useLayoutEffect(() => {
    // initial
    // get the default insured config and get the default plan option
    const defaultInsuredConfig = productConfig?.insured_config?.find(
      (item: any) => item?.insured_person?.value === 'MYSELF'
    );
    const defaultPlanOption = productConfig?.plan_options?.find(
      (item: any) => item?.deductible?.is_default === true
    );
    const defaultPlan = productConfig?.plans?.find((item: any) => item?.is_default === true);
    const initFormBasicParams = {
      gender: defaultInsuredConfig?.insured_person?.default_gender === 'GENDER_M' ? 'M' : 'F',
      age: defaultInsuredConfig?.insured_person?.default_age,
      deductible: defaultPlanOption?.deductible?.value,
      paymentFrequency: 'MONTHLY'
    };
    form.setFieldsValue(initFormBasicParams);
    setQuoteParams({
      ...initFormBasicParams,
      promotionCode: promotionData?.frontendCode
    });
    setSelectedPlan(defaultPlan);
    // init the value of form items
    let AOption = [];
    for (
      let i = defaultInsuredConfig?.insured_person?.min_age;
      i <= defaultInsuredConfig?.insured_person?.max_age;
      i++
    ) {
      AOption.push({
        value: i,
        label: i
      });
    }
    setAgeOption(AOption);
  }, []);

  useEffect(() => {
    if (Object.keys(quoteParams)?.length) {
      fetchQuote();
    }
  }, [quoteParams]);

  const handleGA = useCallback(() => {
    window?.dataLayer?.push({
      event: 'blog-quotecard',
      ecommerce: {
        click: {
          actionField: { list: 'Blog Article' },
          products: [
            {
              id: PRODUCT_ID.MYMILLION,
              name: quoteCardData?.product_identifier?.product_name,
              category: router.query.category,
              brand: 'FWD'
            }
          ]
        }
      }
    });
  }, [quoteCardData, router]);

  const onFinish = useCallback(
    (values: any) => {
      handleGA();
      const { age, gender, deductible, paymentFrequency } = values;
      const { promotionCode } = quoteParams;
      const payment = paymentFrequency === 'MONTHLY' ? 'MONTHLY' : 'ANNUAL';
      const genderParam = gender === 'F' ? 'GENDER_F' : 'GENDER_M';
      const targetUrl = `${
        quoteCardData?.action_link?.url
      }?age=${age}&gender=${genderParam}&deductible=${deductible}&payment=${payment}&utm_source=blog-quote&utm_medium=organic&utm_content=mm-quote-card&promoCode=${
        promotionCode ?? ''
      }`;
      window.location.href = targetUrl;
    },
    [handleGA, quoteCardData, quoteParams]
  );

  return (
    <div className={styles.quote_card_container}>
      <PromotionHeader
        productIdentifier={quoteCardData?.product_identifier}
        bannerDesktop={quoteCardData?.banner_desktop}
        bannerMobile={quoteCardData?.banner_mobile}
        promotion={promotionHeaderData}
        loading={loading}
      />
      <Form
        form={form}
        onFinish={onFinish}
        layout={'vertical'}
        className={styles.quote_card_form}
        onValuesChange={onFormDataChange}
      >
        <Row wrap gutter={isMobile ? [16, 16] : [24, 24]}>
          <Col span={isMobile ? 10 : 12}>
            <Form.Item name="gender" label="性別">
              <CircleRadioGroup type={'Gender'} />
            </Form.Item>
          </Col>
          <Col span={isMobile ? 14 : 12}>
            <Form.Item name="age" label="年齡">
              <SelectBlock optionData={ageOption} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="deductible" style={{ marginBottom: isMobile ? 0 : 8 }}>
          <PriceSliderDeductible marks={productConfig.plan_options} />
        </Form.Item>

        <div className={styles.plan_wrapper}>
          <div className={styles.plan_header}>
            <div className={styles.plan_title}>選擇您的計劃</div>
            <Form.Item name="paymentFrequency">
              {/** TODO: 确认Tooltip的zindex值 */}
              <TimeSwitch
                tooltip={productConfig.payment_frequency.call_out_label_for_payment_frequency}
              />
            </Form.Item>
          </div>
          <div className={styles.plan_list}>
            {productPlans.map((item, index: number) => {
              return (
                <div key={index} className={styles.plan_card}>
                  <PlanCard
                    data={{
                      ...item,
                      sumInsured: form.getFieldValue('sumInsured'),
                      paymentFrequency: form.getFieldValue('paymentFrequency')
                    }}
                    isDeductible={isDeductible}
                    onChange={changePlanCard}
                    isSelected={item?.code === selectedPlan?.code}
                    isLoading={loading}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <Form.Item style={{ marginBottom: 0 }}>
          <LinkButton
            disabled={!selectedPlan?.price || errorQuote === 'error'}
            hideIcon
            isSubmitBtn
            title={quoteCardData?.action_link?.title}
          />
        </Form.Item>
      </Form>
      <div className={styles.quote_brochure}>
        <BrochureLink {...quoteCardData?.product_brochure_link} />
      </div>
      <div className={styles.quote_promotion}>
        <PromotionQuote
          promotionQuote={promotionQuote}
          onPromotionCodeChange={onPromotionCodeChange}
          errorQuote={errorQuote}
          defaultPromotion={promotionData?.frontendCode}
          promotionCode={quoteParams?.promotionCode}
          loading={loading}
        />
      </div>
    </div>
  );
});
export default QuoteCardMyMillion;
